// @flow
import React from 'react';
import cn from 'classnames';
import { Button, Card, CardActions, SelectField, TextField, Cell, CardText } from 'react-md';
import { UploadViewer } from '../upload/UploadViewer';
import type { csvData, csvType } from '../upload/UploadViewer';
import type { schemeType, schemesType, pairs, stringObject, pair } from '../../helpers/schema.helper';

import {
  getEmptyMapping,
  isDisabled,
  isRequired,
  getData,
  testRegex,
  getItemsFromCSV,
  lastValue,
  setDisabledItem,
  updateMapping,
} from '../../helpers/schema.helper';

type Props = {
    category: string,
    fields: Array<string>,
    mandatoryFields: Array<string>,
    depends: stringObject,
    type: string,
    setMapping: (any) => void,
    normalize: (string, string, string) => string,
    getRegexForField: (string, string) => RegExp,
    showToastMessage: (string, string) => void,
    getSchemas: () => Promise<any>,
    postSchemas:
      (schemes: schemesType, mapping: pairs, name: string, category: string)=> Promise<Response>,
    tr: any,
    translationKeys: {
        fileAborted:string,
        importName:string,
        fileSelect: string,
        next: string,
        importDescription: string
    },
    getTranslationMapping: string => string,
}

type State = {
  rawCsv: any,
  items: csvType,
  mapping: pairs,
  schemes: schemesType,
  selectedScheme: number,
  schemeName: string,
  autohide: boolean,
  isFileUpload: boolean,
}

export class Schema extends React.Component<Props, State> {
    static defaultProps = {
      type: 'import',
      category: 'import',
      translationKeys: {
        fileAborted: 'fileAborted!',
        importName: 'importName',
        importDescription: 'importDescription',
        fileSelect: 'fileSelect',
        next: 'next',
      },
      fields: [],
      mandatoryFields: [],
      depends: {},
      getRegexForField: () => null,
      tr: (str: string) => str,
      getTranslationMapping: (str: string) => str,
      normalize: (str: string, char: string, val: string = '') => val,
      postSchemas: (): Promise<any> => Promise.resolve(),
    }
    constructor(props: Props) {
      super(props);

      this.state = {
        isFileUpload: true,
        rawCsv: {},
        items: [],
        mapping: getEmptyMapping(props.fields),
        schemes: [],
        selectedScheme: -1,
        schemeName: '',
        autohide: true,
      };
    }
    componentDidMount() {
      this.props.getSchemas().then(schemes => this.setState({ schemes }));
    }
    render() {
      const { state, props: {
        category, tr, mandatoryFields, fields, setMapping, postSchemas, normalize, getRegexForField,
        depends, translationKeys, getTranslationMapping, showToastMessage,
      } } = this;
      const csv = state.rawCsv;
      const success = tr('address_home_newpasswordrequested_title');
      const close = tr('address_close');
      const schemes = state.schemes.filter(item => category === item.category);
      const dependsVal: string = lastValue(depends);
      // could be renamed to something more generic but usually you mean this to be a countryCode.
      const countryCode = state.mapping[dependsVal].value;
      const padMandatory = (name: string):string => (mandatoryFields.indexOf(name) !== -1 ? '*' : '');
      return this.state.isFileUpload
        ? <UploadViewer
          {... this.props}
          disabled={!state.items.length}
          upload={(data: csvData) =>
            this.setState({ items: getItemsFromCSV(data), rawCsv: data.data })}
          onClick={() => this.setState({ isFileUpload: false })}
        />
        : (
          <div className={'md-grid md-grid--maxwidth'}>
            <Card className={
              cn('pwa-card-form', Cell.getClassName({ size: 12, phoneSize: 4 }))}
            >
              <CardText style={{ position: 'relative' }}>
                <div className={'h-align--center'}>
                  <h1 className={'md-heading-2'}>{tr('address_import_schema_select')}</h1>
                  <p>{tr('address_import_schema_help')}</p>
                </div>
                <div className={'o-grid o-grid--spaced o-grid--column-2'}>
                  <TextField
                    className="o-grid__item"
                    id="schemeName"
                    value={state.schemeName}
                    onChange={val => this.setState({ schemeName: val, selectedScheme: -1 })}
                    placeholder={tr('address_import_schema_name')}
                  />{schemes.length > 0 &&
                  <SelectField
                    className="o-grid__item"
                    id="select-field-1"
                    placeholder={tr('address_import_schema_select')}
                    value={state.schemeName}
                    menuItems={schemes.map((scheme: schemeType) => scheme.name)}
                    onChange={(schemeName: string, idx: number) => {
                      const mapping: pairs = { ...state.mapping };
                      const items = state.items.map((item) => {
                        const clone = { ...item };
                        clone.disabled = false;
                        return clone;
                      });
                      Object.keys(schemes[idx].matches).forEach((match: string) => {
                        const item: ?pair = mapping[match];
                        if (item) {
                          const getSchemeVal = (str: string) => (csv.length > 0 ? csv[0][schemes[idx].matches[str]] : '');
                          const cc = getSchemeVal(dependsVal);
                          item.key = schemes[idx].matches[match];
                          item.value = normalize(match, cc, getSchemeVal(match));
                          item.regex = getRegexForField(match, cc);
                          const i = items.findIndex(o => o.label === item.key);
                          const clone: any = { ...items[i] };
                          clone.disabled = true;
                          items[i] = clone;
                        }
                      });
                      this.setState({ mapping, selectedScheme: idx, schemeName, items });
                    }}
                  />}
                </div>
                <div className={'o-grid o-grid--spaced o-grid--column-2 u-pt--l'}>
                  { csv.length > 0 && fields.map((name: string, idx: number) =>
                    (
                      <div key={name}>
                        <SelectField
                          disabled={isRequired(name, state.mapping, depends)}
                          className="select o-grid__item md-text-field"
                          id={name.concat('-select-field')}
                          menuItems={[''].concat(state.items)}
                          label={padMandatory(name).concat(tr(getTranslationMapping(name)))}
                          placeholder={tr(getTranslationMapping(name))}
                          value={state.mapping[fields[idx]].key}
                          onChange={(value: string) =>
                            this.setState({ ...state,
                              mapping: updateMapping(
                                state.mapping,
                                fields[idx],
                                value,
                                csv[0][value],
                                countryCode,
                                normalize,
                                getRegexForField,
                              ),
                              items: setDisabledItem(
                                value,
                                state.items.slice(0),
                                state.mapping[name]),
                            })}
                        />
                        <TextField
                          id={name}
                          name={name}
                          value={state.mapping[fields[idx]].value || ''}
                          disabled
                          error={!testRegex(state.mapping[fields[idx]])}
                          errorText={tr('address_field_pattern_no_match')}
                        />
                      </div>
                    ),
                  )}
                </div>
                <CardActions className={'h-flex--end'}>
                  <Button flat onClick={() => this.setState({ isFileUpload: true })}>{tr('back')}</Button>
                  <Button
                    id="upload-submit"
                    raised
                    secondary
                    disabled={isDisabled(mandatoryFields, fields, state.mapping)}
                    onClick={() =>
                      postSchemas(state.schemes, state.mapping, state.schemeName, category)
                        .then(() => this.setState(Object.defineProperties(
                          { ...state }, {
                            mapping: { value: getEmptyMapping(fields) },
                            selectedScheme: { value: -1 },
                            schemeName: { value: '' },
                          })))
                        .then(() => {
                          setMapping(getData(state.mapping, state.rawCsv));
                          showToastMessage(success, close);
                        })
                        .catch(e => showToastMessage(e, close))
                    }
                  >
                    {tr(translationKeys.next)}
                  </Button>
                </CardActions>
              </CardText>
            </Card>
          </div>
        );
    }
}
